/**
 * TraineeDashboard Component
 * ORCA360 - Trainee Portal Dashboard
 *
 * Displays list of assessments for trainee to view and acknowledge
 * Features: Pending/acknowledged/archived filters, assessment list, acknowledgment tracking
 *
 * Sprint 4, Day 6: Trainee Portal Implementation
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { LayoutGrid, Clock, CheckCircle2, Archive } from 'lucide-react';

interface Acknowledgment {
  id: string;
  session_id: string;
  acknowledged_at: string;
  self_reflection?: string;
}

interface SessionWithAssessments {
  session_id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  instructor_name: string;
  assessment_count: number;
  avg_score: number;
  pilot_role?: 'captain' | 'first_officer';
  status: 'pending' | 'acknowledged' | 'archived';
  acknowledged_at?: string;
}

type FilterType = 'all' | 'pending' | 'acknowledged' | 'archived';

export default function TraineeDashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionWithAssessments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTraineeData();
  }, []);

  const loadTraineeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Not authenticated');
        return;
      }

      console.log('[TraineeDashboard] Loading data for user:', user.id);

      // Load all published assessments for this trainee
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments_pilot')
        .select(`
          id,
          session_id,
          competency,
          score,
          pilot_role,
          status,
          created_at,
          updated_at,
          session:sessions!assessments_pilot_session_id_fkey (
            session_date,
            aircraft_type,
            flight_phase,
            instructor:instructor_id (
              full_name
            )
          )
        `)
        .eq('trainee_id', user.id)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (assessmentsError) {
        console.error('[TraineeDashboard] Error loading assessments:', assessmentsError);
        throw assessmentsError;
      }

      console.log('[TraineeDashboard] Loaded assessments:', assessments?.length || 0);

      // Load acknowledgments
      const { data: acks, error: acksError } = await supabase
        .from('acknowledgments')
        .select('*')
        .eq('user_id', user.id);

      if (acksError) {
        console.error('[TraineeDashboard] Error loading acknowledgments:', acksError);
        throw acksError;
      }

      console.log('[TraineeDashboard] Loaded acknowledgments:', acks?.length || 0);

      // Create acknowledgment map
      const ackMap = new Map<string, Acknowledgment>();
      acks?.forEach(ack => {
        ackMap.set(ack.session_id, ack);
      });

      // Group assessments by session
      const sessionMap = new Map<string, SessionWithAssessments>();
      
      assessments?.forEach((assessment: any) => {
        const sessionId = assessment.session_id;
        const pilotRole = assessment.pilot_role || 'first_officer';
        const sessionKey = `${sessionId}-${pilotRole}`;
        const session = assessment.session;
        
        if (!session) return;

        const instructorData = Array.isArray(session.instructor) 
          ? session.instructor[0] 
          : session.instructor;

        if (!sessionMap.has(sessionKey)) {
          const ack = ackMap.get(sessionId);
          const isArchived = ack && 
            (new Date().getTime() - new Date(ack.acknowledged_at).getTime()) > 30 * 24 * 60 * 60 * 1000; // 30 days

          sessionMap.set(sessionKey, {
            session_id: sessionId,
            session_date: session.session_date,
            aircraft_type: session.aircraft_type,
            flight_phase: session.flight_phase,
            instructor_name: instructorData?.full_name || 'Unknown',
            assessment_count: 1,
            avg_score: assessment.score,
            pilot_role: pilotRole,
            status: isArchived ? 'archived' : (ack ? 'acknowledged' : 'pending'),
            acknowledged_at: ack?.acknowledged_at,
          });
        } else {
          const existing = sessionMap.get(sessionKey)!;
          existing.assessment_count += 1;
          existing.avg_score = (existing.avg_score * (existing.assessment_count - 1) + assessment.score) / existing.assessment_count;
        }
      });

      const sessionsArray = Array.from(sessionMap.values())
        .sort((a, b) => new Date(b.session_date).getTime() - new Date(a.session_date).getTime());

      setSessions(sessionsArray);
      console.log('[TraineeDashboard] Processed sessions:', sessionsArray.length);

    } catch (err: any) {
      console.error('[TraineeDashboard] Error loading data:', err);
      setError(err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    // Filter by status
    if (filter !== 'all' && session.status !== filter) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        session.aircraft_type.toLowerCase().includes(searchLower) ||
        session.instructor_name.toLowerCase().includes(searchLower) ||
        session.flight_phase.toLowerCase().includes(searchLower) ||
        session.session_date.includes(searchTerm)
      );
    }

    return true;
  });

  const getStatusBadge = (status: 'pending' | 'acknowledged' | 'archived') => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded">Pending Review</span>;
      case 'acknowledged':
        return <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded">✓ Acknowledged</span>;
      case 'archived':
        return <span className="px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded">Archived</span>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 4.5) return 'text-green-600 bg-green-50';
    if (score >= 3.5) return 'text-blue-600 bg-blue-50';
    if (score >= 2.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const stats = {
    total: sessions.length,
    pending: sessions.filter(s => s.status === 'pending').length,
    acknowledged: sessions.filter(s => s.status === 'acknowledged').length,
    archived: sessions.filter(s => s.status === 'archived').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ice">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orca-elevate"></div>
            <p className="mt-4 text-steel">Loading your assessments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ice">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-medium">Error loading assessments</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadTraineeData}
              className="mt-3 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-navy">My Assessments</h1>
          <p className="text-steel mt-1">
            Review your training assessments and track your progress
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-steel mb-1">Total Sessions</div>
            <div className="text-2xl font-bold text-navy">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-steel mb-1">Pending Review</div>
            <div className="text-2xl font-bold text-red-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-steel mb-1">Acknowledged</div>
            <div className="text-2xl font-bold text-green-600">{stats.acknowledged}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-steel mb-1">Archived</div>
            <div className="text-2xl font-bold text-gray-600">{stats.archived}</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Filter Buttons */}
            <div className="flex gap-2 flex-wrap overflow-x-auto">
              {([
                { key: 'all',          Icon: LayoutGrid,   label: 'All',          count: stats.total,       active: 'bg-orca-elevate text-white' },
                { key: 'pending',      Icon: Clock,        label: 'Pending',      count: stats.pending,     active: 'bg-red-600 text-white' },
                { key: 'acknowledged', Icon: CheckCircle2, label: 'Acknowledged', count: stats.acknowledged, active: 'bg-green-600 text-white' },
                { key: 'archived',     Icon: Archive,      label: 'Archived',     count: stats.archived,    active: 'bg-gray-600 text-white' },
              ] as const).map(({ key, Icon, label, count, active }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  title={`${label} (${count})`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                    filter === key ? active : 'bg-steel/10 text-steel hover:bg-steel/20'
                  }`}
                >
                  <Icon size={15} />
                  <span className="hidden sm:inline">{label}</span>
                  <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${
                    filter === key ? 'bg-white/25' : 'bg-steel/15'
                  }`}>{count}</span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by aircraft, instructor, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
              />
            </div>
          </div>
        </div>

        {/* Assessment List */}
        {filteredSessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-steel text-lg">No assessments found</p>
            <p className="text-steel/70 text-sm mt-1">
              {filter !== 'all' 
                ? 'Try changing the filter or search term' 
                : 'Your assessments will appear here once published by your instructor'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <div
                key={`${session.session_id}-${session.pilot_role || 'pilot'}`}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/trainee/assessment/${session.session_id}`)}
              >
                <div className="p-5">

                  {/* Row 1: date · status (centered) · score */}
                  <div className="grid grid-cols-3 items-center mb-2">
                    <span className="text-xs font-semibold text-steel uppercase tracking-wide">
                      {format(new Date(session.session_date), 'MMM dd, yyyy')}
                    </span>
                    <div className="flex justify-center">
                      {getStatusBadge(session.status)}
                    </div>
                    <div className="flex justify-end">
                      <div className={`text-base font-bold px-2.5 py-0.5 rounded-md leading-tight ${getScoreColor(session.avg_score)}`}>
                        {session.avg_score.toFixed(1)}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: session identity (what) */}
                  <h3 className="text-base font-semibold text-navy mb-2 leading-snug">
                    {session.aircraft_type}
                    <span className="text-steel font-normal"> · </span>
                    {session.flight_phase}
                  </h3>

                  {/* Row 3: supporting info */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-steel">
                    <span>{session.instructor_name}</span>
                    <span className="text-steel/40">·</span>
                    <span>{session.assessment_count} competencies</span>
                    {session.pilot_role && (
                      <>
                        <span className="text-steel/40">·</span>
                        <span>{session.pilot_role === 'captain' ? 'Captain' : 'First Officer'}</span>
                      </>
                    )}
                  </div>

                  {/* Row 4: acknowledged timestamp (secondary, only when relevant) */}
                  {session.acknowledged_at && (
                    <div className="mt-2 text-xs text-steel/60">
                      Acknowledged {format(new Date(session.acknowledged_at), 'MMM dd, yyyy · h:mm a')}
                    </div>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
